import { DomainError } from "./errors";
import { ML_MAX_AMOUNT_CENTS, decimalAmountCents, type MarketplaceOrderAction } from "./marketplace";

/**
 * Regras puras dos canais de venda da T3.3 (RF-VEN-09): Magalu, Amazon, Nuvemshop e Shopify. O resto (contas,
 * anúncios, pedidos, sincronização) é o mesmo dos marketplaces (marketplace.ts). Valores em inteiros: texto decimal
 * vira centavos pela representação; o `amount`/`normalizer` da Magalu por divisão inteira.
 */

export const CHANNEL_PROVIDER_IDS = ["magalu", "amazon", "nuvemshop", "shopify"] as const;
export type ChannelProvider = (typeof CHANNEL_PROVIDER_IDS)[number];
export const isChannelProvider = (v: unknown): v is ChannelProvider =>
  typeof v === "string" && (CHANNEL_PROVIDER_IDS as readonly string[]).includes(v);

/** Marketplace Brasil da Amazon (região NA da SP-API). */
export const AMAZON_MARKETPLACE_BR = "A2Q3Y263D00KWC";
/** Páginas por mensagem do polling e da importação de anúncios. */
export const CHANNEL_POLL_MAX_PAGES = 3;
/** Folga sobre o último visto e primeira janela de uma conta nova. */
export const CHANNEL_POLL_OVERLAP_MS = 15 * 60_000;
export const CHANNEL_POLL_FIRST_MS = 24 * 3600_000;
/** Páginas máximas de uma varredura do polling (passou = encerra e registra). */
export const CHANNEL_POLL_MAX_SCAN_PAGES = 200;

type Rule = { import: string[]; wait: string[]; cancel: string[] };
const RULES: Record<ChannelProvider, Rule> = {
  nuvemshop: {
    import: ["paid"],
    wait: ["pending", "authorized", "partially_paid", "open"],
    cancel: ["cancelled", "voided", "refunded", "abandoned"],
  },
  shopify: {
    import: ["PAID"],
    wait: ["PENDING", "AUTHORIZED", "PARTIALLY_PAID"],
    cancel: ["CANCELLED", "REFUNDED", "VOIDED", "EXPIRED"],
  },
  amazon: {
    import: ["UNSHIPPED", "PARTIALLY_SHIPPED", "SHIPPED"],
    wait: ["PENDING"],
    cancel: ["CANCELLED"],
  },
  magalu: {
    import: ["approved", "invoiced", "shipped", "delivered", "finished"],
    wait: ["new", "created"],
    cancel: ["cancelled"],
  },
};

/** O que fazer com o pedido conforme a situação no canal (só pago vira pedido de venda). */
export function channelOrderAction(p: ChannelProvider, status: string): MarketplaceOrderAction {
  const r = RULES[p];
  if (r.import.includes(status)) return "import";
  if (r.wait.includes(status)) return "wait";
  if (r.cancel.includes(status)) return "cancel";
  return "review";
}

const LABELS: Record<ChannelProvider, Readonly<Record<string, string>>> = {
  nuvemshop: {
    open: "Aberto",
    pending: "Aguardando pagamento",
    authorized: "Pagamento autorizado",
    partially_paid: "Pago parcialmente",
    paid: "Pago",
    abandoned: "Abandonado",
    refunded: "Reembolsado",
    partially_refunded: "Reembolsado parcialmente",
    voided: "Pagamento anulado",
    cancelled: "Cancelado",
  },
  shopify: {
    PENDING: "Aguardando pagamento",
    AUTHORIZED: "Pagamento autorizado",
    PARTIALLY_PAID: "Pago parcialmente",
    PAID: "Pago",
    PARTIALLY_REFUNDED: "Reembolsado parcialmente",
    REFUNDED: "Reembolsado",
    VOIDED: "Pagamento anulado",
    EXPIRED: "Pagamento expirado",
    CANCELLED: "Cancelado",
  },
  amazon: {
    PENDING: "Aguardando pagamento",
    UNSHIPPED: "Pago (aguardando envio)",
    PARTIALLY_SHIPPED: "Enviado parcialmente",
    SHIPPED: "Enviado",
    CANCELLED: "Cancelado",
    UNFULFILLABLE: "Não atendível",
  },
  magalu: {
    new: "Novo (aguardando aprovação)",
    created: "Criado (aguardando aprovação)",
    approved: "Aprovado",
    invoiced: "Faturado",
    shipped: "Enviado",
    delivered: "Entregue",
    finished: "Concluído",
    cancelled: "Cancelado",
  },
};

export function channelOrderStatusLabel(p: ChannelProvider, status: string): string {
  const l = LABELS[p];
  return Object.hasOwn(l, status) ? l[status]! : status;
}

/** Nuvemshop: pedido cancelado = `cancelled`; senão a situação do pagamento (sem ela, a do pedido). */
export const nuvemshopOrderStatus = (status: string, paymentStatus: string): string =>
  status === "cancelled" ? "cancelled" : paymentStatus || status;

/** Shopify: cancelado (`cancelledAt`) = `CANCELLED`; senão `displayFinancialStatus` (ausente = aguardando). */
export const shopifyOrderStatus = (cancelledAt: string | null, financial: string | null): string =>
  cancelledAt ? "CANCELLED" : financial || "PENDING";

/** Texto/número decimal de um canal em centavos (até 2 casas). `source`: "da Nuvemshop", "da Shopify"… */
export const channelAmountCents = (v: number | string, source: string): number =>
  decimalAmountCents(v, source);

const NORMALIZERS = new Set([1, 10, 100, 1000, 10_000]);

/** Magalu: `amount` inteiro ÷ `normalizer` (potência de 10 até 10.000) em centavos, meio para cima. */
export function normalizedAmountCents(amount: number, normalizer: number): number {
  if (!Number.isSafeInteger(amount) || amount < 0 || !NORMALIZERS.has(normalizer))
    throw new DomainError("invalid_amount", `Valor inválido do Magalu: ${amount}/${normalizer}`);
  let cents: number;
  if (normalizer <= 100) cents = amount * (100 / normalizer);
  else {
    const d = normalizer / 100;
    cents = Math.floor(amount / d) + ((amount % d) * 2 >= d ? 1 : 0);
  }
  if (!Number.isSafeInteger(cents) || cents > ML_MAX_AMOUNT_CENTS)
    throw new DomainError("invalid_amount", `Valor do Magalu acima do limite: ${amount}/${normalizer}`);
  return cents;
}

/**
 * Amazon traz o total do item: unitário = total ÷ quantidade (meio para cima) e a diferença em centavos
 * (total − unitário × quantidade), que vai para a observação do pedido.
 */
export function amazonUnitPrice(totalCents: number, qty: number): { unitCents: number; diffCents: number } {
  if (!Number.isSafeInteger(qty) || qty <= 0 || !Number.isSafeInteger(totalCents))
    throw new DomainError("invalid_amount", "Quantidade ou valor inválido da Amazon");
  const unitCents = Math.floor((2 * totalCents + qty) / (2 * qty));
  return { unitCents, diffCents: totalCents - unitCents * qty };
}

const SHOP_RE = /^[a-z0-9][a-z0-9-]{0,59}\.myshopify\.com$/;

/** Domínio da loja Shopify a partir do que o usuário digitou ("minhaloja", "minhaloja.myshopify.com", URL). */
export function shopifyShopDomain(input: string): string | null {
  let s = input.trim().toLowerCase();
  if (!s) return null;
  s = s.replace(/^https?:\/\//, "").split("/")[0]!;
  if (!s.includes(".")) s = `${s}.myshopify.com`;
  return SHOP_RE.test(s) ? s : null;
}

export const shopifyGid = (type: string, id: string): string => `gid://shopify/${type}/${id}`;

/** Id numérico de um gid do tipo esperado; outro tipo ou formato = null. */
export function shopifyGidId(gid: unknown, type: string): string | null {
  if (typeof gid !== "string") return null;
  const m = /^gid:\/\/shopify\/([A-Za-z]+)\/(\d{1,20})$/.exec(gid);
  return m && m[1] === type ? m[2]! : null;
}

const esc = (s: string, key = false) => {
  const v = s.replace(/%/g, "%25").replace(/&/g, "%26");
  return key ? v.replace(/=/g, "%3D") : v;
};

/** Texto assinado do retorno do OAuth da Shopify: parâmetros (menos `hmac`) ordenados, `k=v` unidos por `&`. */
export function shopifyOAuthMessage(params: [string, string][]): string {
  return params
    .filter(([k]) => k !== "hmac")
    .map(([k, v]) => `${esc(k, true)}=${esc(v)}`)
    .sort()
    .join("&");
}

/** Janela do polling por data de atualização: do último visto − folga (primeira vez: último dia) até agora. */
export function channelPollWindow(lastSeenIso: string | null, now: number): { from: string; to: string } {
  const seen = lastSeenIso ? Date.parse(lastSeenIso) : Number.NaN;
  const fromMs = Number.isNaN(seen)
    ? now - CHANNEL_POLL_FIRST_MS
    : Math.min(seen, now) - CHANNEL_POLL_OVERLAP_MS;
  return { from: new Date(fromMs).toISOString(), to: new Date(now).toISOString() };
}

const ORDER_ID_RE: Record<ChannelProvider, RegExp> = {
  nuvemshop: /^\d{1,20}$/,
  shopify: /^\d{1,20}$/,
  amazon: /^\d{3}-\d{7}-\d{7}$/,
  magalu: /^[A-Za-z0-9-]{1,64}$/,
};

/** Id do pedido no formato do canal (vai na URL das chamadas). */
export const isChannelOrderId = (p: ChannelProvider, v: unknown): v is string =>
  typeof v === "string" && ORDER_ID_RE[p].test(v);
